import { alpha, type SxProps, type Theme } from "@mui/material/styles";

export const ownerCard: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.5),
  bgcolor: alpha(theme.palette.primary.main, 0.06),
  border: 1,
  borderColor: "primary.main",
  borderRadius: 2,
  p: { xs: 1.5, md: 1.75 },
});

export const ownerIcon: SxProps<Theme> = {
  width: { xs: 38, md: 44 },
  height: { xs: 38, md: 44 },
  borderRadius: 1.5,
  bgcolor: "primary.main",
  color: "primary.contrastText",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  flex: "none",
};

export const ownerBadge: SxProps<Theme> = {
  fontSize: { xs: "0.625rem", md: "0.6875rem" },
  fontWeight: 700,
  color: "secondary.contrastText",
  bgcolor: "secondary.main",
  px: 1.5,
  py: 0.5,
  borderRadius: 2.5,
  flex: "none",
};

export const section: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
});

export const sectionTitle: SxProps<Theme> = {
  fontSize: { xs: "0.75rem", md: "0.8125rem" },
  fontWeight: 700,
};

export const roleGrid: SxProps<Theme> = (theme) => ({
  display: "grid",
  gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
  gap: theme.spacing(1.25),
});

export const roleCard =
  (selected: boolean): SxProps<Theme> =>
  (theme) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    p: theme.spacing(1.5, 1.75),
    border: 1,
    borderColor: selected ? "primary.main" : "divider",
    borderRadius: 1.75,
    bgcolor: selected ? alpha(theme.palette.primary.main, 0.08) : "transparent",
    boxShadow: selected ? `inset 0 0 0 1px ${theme.palette.primary.main}` : "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: "primary.main",
      bgcolor: alpha(theme.palette.primary.main, 0.04),
    },
  });

export const sectionDivider: SxProps<Theme> = (theme) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.75),
  borderTop: 1,
  borderColor: "divider",
  pt: { xs: 1.75, md: 2.25 },
});

export const inviteRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "flex-end",
  gap: theme.spacing(1.5),
  flexDirection: { xs: "column", md: "row" },
});

export const outlinedButton: SxProps<Theme> = {
  minWidth: 140,
  height: 56,
  fontWeight: 600,
  width: { xs: "100%", md: 140 },
};

export const roleHint: SxProps<Theme> = {
  fontSize: { xs: "0.65625rem", md: "0.71875rem" },
  color: "secondary.main",
};

export const dividerRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  my: 0.25,
});

export const dividerLine: SxProps<Theme> = {
  flex: 1,
  height: 1,
  bgcolor: "divider",
};

export const dividerLabel: SxProps<Theme> = {
  fontSize: { xs: "0.65625rem", md: "0.6875rem" },
  color: "text.secondary",
};

export const memberRow: SxProps<Theme> = (theme) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1.25),
  bgcolor: { xs: "background.paper", md: "background.default" },
  borderRadius: 1.5,
  p: { xs: 1.25, md: 1.5 },
});

export const memberAvatar =
  (color: "info" | "warning" | "success" | "secondary"): SxProps<Theme> => ({
    width: { xs: 30, md: 34 },
    height: { xs: 30, md: 34 },
    borderRadius: 1.25,
    bgcolor: `${color}.main`,
    color: "common.white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: "none",
  });

export const emptyMembers: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: { xs: 0.75, md: 1 },
  textAlign: "center",
  border: "2px dashed",
  borderColor: "divider",
  borderRadius: 1.75,
  p: { xs: 2, md: 2.5 },
  mt: 0.5,
};

export const invitedTitle: SxProps<Theme> = {
  fontSize: { xs: "0.6875rem", md: "0.75rem" },
  fontWeight: 700,
  color: "text.secondary",
};

export const inviteLinkTitle: SxProps<Theme> = {
  fontSize: { xs: "0.75rem", md: "0.78125rem" },
  fontWeight: 600,
};

export const ownerContent: SxProps<Theme> = {
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: 0.25,
};

export const ownerName: SxProps<Theme> = {
  fontSize: { xs: "0.78125rem", md: "0.84375rem" },
  fontWeight: 700,
};

export const ownerAccess: SxProps<Theme> = {
  fontSize: { xs: "0.65625rem", md: "0.71875rem" },
  color: "text.secondary",
};

export const mobileOnly: SxProps<Theme> = {
  display: { xs: "inline", md: "none" },
};

export const desktopOnly: SxProps<Theme> = {
  display: { xs: "none", md: "inline" },
};

export const roleContent: SxProps<Theme> = {
  flex: 1,
};

export const roleTitle: SxProps<Theme> = {
  fontSize: { xs: "0.78125rem", md: "0.8125rem" },
  fontWeight: 600,
};

export const roleDescription: SxProps<Theme> = {
  fontSize: { xs: "0.65625rem", md: "0.6875rem" },
  color: "text.secondary",
  lineHeight: 1.5,
};

export const inviteField: SxProps<Theme> = {
  flex: 1,
  width: "100%",
};

export const inviteLinkSection: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

export const invitedList: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
  mt: 0.5,
};

export const memberContent: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const memberPhone: SxProps<Theme> = {
  fontSize: { xs: "0.75rem", md: "0.78125rem" },
  fontWeight: 600,
};

export const memberMeta: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 0.75,
  mt: 0.375,
};

export const memberPending: SxProps<Theme> = {
  fontSize: { xs: "0.625rem", md: "0.65625rem" },
  color: "text.secondary",
};

export const removeButton: SxProps<Theme> = {
  flex: "none",
};

export const emptyTitle: SxProps<Theme> = {
  fontSize: { xs: "0.75rem", md: "0.78125rem" },
  fontWeight: 600,
};

export const emptyDescription: SxProps<Theme> = {
  fontSize: { xs: "0.65625rem", md: "0.71875rem" },
  color: "text.secondary",
  lineHeight: 1.7,
  maxWidth: 320,
};
